/**
 * Запись голосового сообщения.
 *
 * Три вещи, которые здесь неочевидны.
 *
 * 1. **Запись идёт в webm, а уходит в ogg.** Chromium в Ogg писать не умеет, а
 *    Telegram голосовые принимает только в нём. Пакеты Opus при этом одни и те
 *    же, различается контейнер, поэтому перед отправкой запись перепаковывается
 *    (lib/opus-ogg.ts) — без перекодирования и без потери качества.
 *
 * 2. **Дорожка микрофона глушится явно.** Остановки MediaRecorder мало: поток
 *    остаётся живым, и в macOS продолжает гореть индикатор записи, а Windows
 *    держит устройство занятым. Для приложения, которое работает с рабочими
 *    аккаунтами компании, «микрофон вроде выключен» — недопустимая
 *    формулировка.
 *
 * 3. **Записанное не уходит само.** Между «отпустил кнопку» и «отправлено»
 *    стоит прослушивание и явное подтверждение: получателем будет живой
 *    человек в рабочем чате, и случайная отправка неудачного дубля не
 *    отменяется — Telegram даёт удалить сообщение, но собеседник его уже
 *    увидел.
 */

import { useEffect, useRef, useState } from "react";
import { webmToOggOpus } from "../lib/opus-ogg.js";
import { MicIcon, SendIcon, TrashIcon } from "./icons.tsx";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface VoiceRecorderProps {
  disabled: boolean;
  /**
   * Готовое голосовое в формате Telegram и его длительность в секундах.
   *
   * Длительность обязана уйти вместе с файлом. Здесь была ошибка ровно
   * наоборот: считалось, что её определит TDLib. Он этого не делает — берёт
   * переданное значение, а ноль отправляет как есть. Голосовое приходило
   * собеседнику пустым, «0:00», и не проигрывалось: со стороны это выглядело
   * так, будто запись не сохранилась.
   *
   * Число берётся из самого файла (по пакетам Opus), а не из экранного
   * таймера: таймер тикает раз в секунду и к концу записи расходится с
   * настоящей длиной на доли секунды, а у Telegram по этому числу рисуется
   * дорожка воспроизведения.
   */
  onRecorded: (file: File, seconds: number) => void | Promise<void>;
}

type Phase = "idle" | "recording" | "preview";

/** Потолок длительности. Telegram примет и больше, но это уже не голосовое. */
const MAX_SECONDS = 300;

export function VoiceRecorder({ disabled, onRecorded }: VoiceRecorderProps) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{
    url: string;
    file: File;
    seconds: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Отпустить микрофон. Вызывается из всех веток, включая размонтирование. */
  const releaseMic = (): void => {
    for (const track of stream.current?.getTracks() ?? []) track.stop();
    stream.current = null;
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      releaseMic();
      // Уход с экрана посреди записи не должен оставлять blob висеть в памяти.
      if (preview) URL.revokeObjectURL(preview.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async (): Promise<void> => {
    setError(null);
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        // Обработка включена: запись идёт из рабочего помещения, а не из студии.
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      stream.current = media;
      chunks.current = [];

      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const rec = new MediaRecorder(media, { mimeType: mime });
      recorder.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };
      rec.onstop = () => void finish();
      rec.start();

      setSeconds(0);
      setPhase("recording");
      timer.current = setInterval(() => {
        setSeconds((v) => {
          // Останов по потолку внутри таймера, а не отдельным эффектом:
          // так момент остановки совпадает с моментом, который видит человек.
          if (v + 1 >= MAX_SECONDS) recorder.current?.stop();
          return v + 1;
        });
      }, 1000);
    } catch (err) {
      releaseMic();
      setPhase("idle");
      setError(
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Доступ к микрофону не разрешён"
          : "Не удалось включить микрофон",
      );
    }
  };

  const finish = async (): Promise<void> => {
    releaseMic();
    setBusy(true);
    try {
      const webm = new Blob(chunks.current, { type: "audio/webm" });
      const ogg = await webmToOggOpus(webm);
      const file = new File([ogg.blob], `voice-${Date.now()}.ogg`, {
        type: "audio/ogg",
      });

      setPreview({
        url: URL.createObjectURL(ogg.blob),
        file,
        // Округление вверх: полусекундная запись — это «1», а не «0», а ноль
        // Telegram показал бы как пустое голосовое.
        seconds: Math.max(1, Math.round(ogg.seconds)),
      });
      setPhase("preview");
    } catch (err) {
      setPhase("idle");
      setError(
        err instanceof Error
          ? `Запись не удалось подготовить: ${err.message}`
          : "Запись не удалась",
      );
    } finally {
      setBusy(false);
    }
  };

  const discard = (): void => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setPhase("idle");
    setSeconds(0);
  };

  const confirm = async (): Promise<void> => {
    if (!preview) return;
    setBusy(true);
    try {
      await onRecorded(preview.file, preview.seconds);
      discard();
    } catch {
      // Ошибку отправки показывает композер: запись остаётся, чтобы её можно
      // было отправить повторно, а не записывать заново.
    } finally {
      setBusy(false);
    }
  };

  if (phase === "idle") {
    return (
      <>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="flex-none text-muted-foreground hover:text-brand"
          disabled={disabled}
          onClick={() => void start()}
          aria-label="Записать голосовое сообщение"
          title="Записать голосовое"
        >
          <MicIcon className="size-[18px]" />
        </Button>
        {error && (
          <span role="alert" className="text-xs text-destructive">
            {error}
          </span>
        )}
      </>
    );
  }

  if (phase === "recording") {
    return (
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-destructive/40 bg-destructive/10 px-3 py-1.5">
        <span
          aria-hidden
          className="status-dot animate-pulse text-destructive"
        />
        <span className="flex-none text-sm font-medium tabular-nums text-destructive">
          {format(seconds)}
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          Идёт запись — остановите, чтобы прослушать
        </span>
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="flex-none"
          onClick={() => recorder.current?.stop()}
        >
          Стоп
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-card px-2 py-1.5">
      {/* Прослушать до отправки обязательно: получатель — живой человек, и
          отменить отправленное нельзя. */}
      <audio
        controls
        src={preview?.url}
        className={cn("h-8 min-w-0 flex-1", busy && "opacity-50")}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="flex-none text-muted-foreground hover:text-destructive"
        onClick={discard}
        disabled={busy}
        aria-label="Удалить запись"
        title="Удалить запись"
      >
        <TrashIcon className="size-4" />
      </Button>
      <Button
        type="button"
        size="icon-sm"
        className="flex-none"
        onClick={() => void confirm()}
        disabled={busy}
        aria-label="Отправить голосовое"
        title="Отправить голосовое"
      >
        <SendIcon className="size-4" />
      </Button>
    </div>
  );
}

function format(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}
